import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Car, Cast, MapPin, Radio, Users, UsersRound, X } from "lucide-react";
import type { ComponentType } from "react";
import { useProtectedNavigate } from "@/hooks/useProtectedNavigate";

interface MoreItem {
  key: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  requiresAuth?: boolean;
}

const MORE_ITEMS: MoreItem[] = [
  { key: "tracks", to: "/tracks", icon: MapPin },
  { key: "teams", to: "/teams", icon: UsersRound },
  { key: "drivers", to: "/drivers", icon: Users },
  { key: "vehicles", to: "/vehicles", icon: Car, requiresAuth: true },
  { key: "liveStreams", to: "/live-streams", icon: Radio, requiresAuth: true },
  { key: "trackcast", to: "/trackcast", icon: Cast },
];

interface MoreSheetProps {
  onClose: () => void;
}

export default function MoreSheet({ onClose }: MoreSheetProps) {
  const { t } = useTranslation("common");
  const protectedNavigate = useProtectedNavigate();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Same contract as ui/Modal: Escape closes; the close button takes focus
  // on open so keyboard users start inside the dialog.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSelect = (item: MoreItem) => {
    protectedNavigate(item.to, item.requiresAuth);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("navigation.more")}
        className="fixed bottom-0 inset-x-0 z-50 rounded-t-2xl bg-white border-t border-secondary-200 lg:hidden pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <span className="text-sm font-semibold text-secondary-900">
            {t("navigation.more")}
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label={t("navigation.closeMoreSheet")}
            className="p-2 rounded-lg text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 px-4 pt-1 pb-5">
          {MORE_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => handleSelect(item)}
              className="flex flex-col items-center gap-1.5 rounded-xl p-3 text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
                <item.icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="text-xs font-medium">
                {t(`navigation.${item.key}`)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
